import Phaser from 'phaser';
import { biomeForLevel } from '../../content/biomes';
import { NAME_NOUNS } from '../../content/names';
import type { DuelLawn, RaceSize } from '../../gen/modes';
import { GreedyPlanner } from '../../gen/solver/greedyPlanner';
import { HerdPilot } from '../../gen/solver/herdBot';
import { MazePilot } from '../../gen/solver/mazeBot';
import { MazeStar } from '../render/MazeStar';
import { SoccerPilot } from '../../gen/solver/soccerBot';
import { equippedPaint, equippedStripes } from '../../meta/economy';
import {
  matchLawn,
  matchRulesFor,
  playMatch,
  playMatchTo,
  startMatch,
} from '../../meta/online/duel';
import { knownPaints } from '../../meta/online/live';
import { matchPay, placesFrom, RACE_PAID_PER_DAY, type Seat } from '../../meta/online/race';
import { haptics } from '../../platform/haptics';
import { TICK_RATE } from '../../sim/constants';
import { dx, dy } from '../../sim/direction';
import type { SimEvent } from '../../sim/events';
import type { Intent } from '../../sim/mower';
import { encodeIntent, hashSimState } from '../../sim/replay';
import type { Simulation } from '../../sim/simulation';
import { gameAudio } from '../audio/audio';
import { exposeDebug } from '../debug';
import { InputController } from '../input/InputController';
import { HUD_HEIGHT, TILE_SIZE } from '../layout';
import { currentRace, type MatchResult, type RaceSession, type RaceView } from '../live';
import { addFriend, isFriend, myFriends } from '../online';
import { saves, todayKey } from '../records';
import { mowerPixelPosition } from '../render/interpolate';
import { DIZZY_SPIN_MS, LevelView, MOWER_DEPTH } from '../render/LevelView';
import { paintedMowerTexture } from '../render/cosmeticArt';
import { RING_1, RING_2, SpriteOutline } from '../render/outline';
import { mowerTextureKey } from '../render/placeholderArt';
import { seatView } from '../render/seatView';
import { loadControlScheme } from '../settings';
import { label, UI } from '../ui/text';
import { lawnThumbnail, navyPanel } from '../ui/chrome';
import type { HudScene } from './HudScene';
import type { ResultsModel } from './ResultsScene';
import { wipeTo } from './WipeScene';
import { nicknameOf, whoText } from '../nicknames';

/** Where a match starts: its lawn (seed and size) and its first tick on this phone's clock. */
export interface DuelLevelData {
  seed: string;
  size: RaceSize;
  atLocal: number;
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;
/** 1st, 2nd, 3rd, 4th. */
const ordinal = (n: number) => `${n}${['st', 'nd', 'rd'][n - 1] ?? 'th'}`;
/** A hedge maze's result line: how far from the middle a mower ended. */
const toGo = (tiles: number) =>
  tiles === 0 ? 'in the middle' : `${plural(tiles, 'tile')} from the middle`;

const BACKGROUND = '#2b4a26';
/** Progress (with the state's hash) to the room every this many ticks: four a second. */
const PROGRESS_EVERY = 15;
/**
 * The friend's mower wears its owner's paint inside a red outline (owner, 2026-10-26: the first
 * blue was too faint). The grass it mows is hatched with faint red diagonals inside a faint red
 * edge, quieter than the mower itself (owner: the bold edge was too much). A red wash would read
 * as dead grass. Red and green look alike to the commonest color blindness, so each mark also
 * differs from the grass in lightness: the red outline has a pale ring outside it, and the grass
 * marks are a dark red.
 */
const FRIEND_RED = 0xe8413c;
const FRIEND_HALO = 0xfff1e8;
/**
 * A party's other mowers (docs/PARTY.md), by seat order after this player's own: red, blue,
 * purple; their grass in a dark shade of the same.
 */
const OTHER_COLORS = [FRIEND_RED, 0x3c8de8, 0xa65de8] as const;
const OTHER_GRASS = [0x6e1712, 0x123a6e, 0x46166e] as const;
/** Soccer two against two: this player's teammate, in green (the other team red and blue). */
const TEAMMATE_GREEN = 0x5fd35a;
const TEAMMATE_GRASS = 0x1d5a1a;
/** Soccer two against two: the other team's mowers, see-through as a race's ghost is. */
const RIVAL_ALPHA = 0.55;

/** Another mower in the match: its seat, colors, sprite, outline and grass hatching. */
interface Other {
  seat: Seat;
  color: number;
  grass: number;
  mower: Phaser.GameObjects.Sprite;
  outline: SpriteOutline;
  hatching: Phaser.GameObjects.Blitter;
}
/**
 * This player's mower is outlined too, in the "YOU" tag's yellow, so the red one does not grab all
 * the attention (owner, build 26).
 */
const MINE_OUTLINE = UI.accent;
/** A diagonal line every this many pixels; it divides the tile, so lines run on across tiles. */
const HATCH_GAP = 4;
const HATCH_ALPHA = 0.35;
const FRIEND_EDGE_ALPHA = 0.4;
const HATCH_KEY = 'duel-hatch';
/** How long the "YOU" marker stays over this player's mower once the match is on (ms). */
const YOU_MARK_MS = 2500;
/** A round-up's "into YOUR pen" caption stays this long after GO. */
const RULES_CAPTION_MS = 4000;
/** A hedge maze's star on the maps, in white (this player's own color is yellow). */
const PLAZA_MARK = 0xfff8e0;

type Phase = 'countdown' | 'playing' | 'over' | 'result';

/**
 * Head to head on one lawn (docs/HEAD_TO_HEAD.md): both mowers in one match, this phone's copy
 * of it. This player's turns play at once and go to the room; the room sends every turn back
 * stamped with its final tick, and when one lands on a tick already played (the friend's, a few
 * frames late) the match is rebuilt from the start with every turn known and the lawn redrawn.
 * The clock is real time from the room's start (one lawn, one clock: no pause). The result is the
 * room's own replay.
 */
export class DuelLevelScene extends Phaser.Scene {
  private session!: RaceSession;
  private data_!: DuelLevelData;
  private lawn!: DuelLawn;
  /** The match as this phone knows it, and the same seen from this player's seat. */
  private match!: Simulation;
  private mine!: Simulation;
  private you: Seat = 0;
  private view!: LevelView;
  /**
   * Every other mower in the match (one in a match of two; up to three in a party): its seat, its
   * sprite in its owner's paint, its outline (its color a pixel out, a pale ring outside that), and
   * the hatching of the grass it mowed.
   */
  private others: Other[] = [];
  /** "YOU" over this player's mower through the countdown and the first moments. */
  private youMark: Phaser.GameObjects.Container | null = null;
  /** A round-up: "YOURS" over this player's pen, shown as long as the YOU mark. */
  private penTag: Phaser.GameObjects.Container | null = null;
  private youMarkUntil = 0;
  private grassMarks!: Phaser.GameObjects.Graphics;
  /** Stars circling a dizzy mower's head (either seat's). */
  private stars!: Phaser.GameObjects.Graphics;
  private phase: Phase = 'countdown';
  private shownCount = '';
  private queuedTurn: Intent | null = null;
  private lastProgress = 0;
  private autopilot = false;
  private planner: GreedyPlanner | null = null;
  /** Mower soccer (docs/SOCCER_MATCH.md): the autopilot kicks for the far goal. */
  private kicker: SoccerPilot | null = null;
  /** A sheep round-up (docs/ROUNDUP_MATCH.md): the autopilot herds for this player's pen. */
  private herder: HerdPilot | null = null;
  /** A hedge maze (docs/MAZE_MATCH.md): the autopilot heads for the middle. */
  private mazer: MazePilot | null = null;
  /** A hedge maze: the whole maze on show through the countdown (then gone). */
  private peek: Phaser.GameObjects.Container | null = null;
  /** A hedge maze: the diamond star floating over the middle (the first to touch it wins). */
  private star: MazeStar | null = null;
  private settled = false;
  private noticed = { left: false, line: false };
  private unsubscribe: (() => void) | null = null;
  private resultCard: ResultsModel | null = null;

  constructor() {
    super('DuelLevel');
  }

  init(data: DuelLevelData): void {
    this.data_ = data;
    this.phase = 'countdown';
    this.shownCount = '';
    this.queuedTurn = null;
    this.lastProgress = 0;
    this.planner = null;
    this.kicker = null;
    this.settled = false;
    this.noticed = { left: false, line: false };
    this.resultCard = null;
    this.autopilot =
      import.meta.env.DEV && new URLSearchParams(window.location.search).has('autopilot');
  }

  create(): void {
    const session = currentRace();
    if (!session) {
      wipeTo(this, 'Versus');
      return;
    }
    this.session = session;
    // Fresh friends for the result's ADD FRIEND (a request may have been accepted since).
    void myFriends();
    this.you = session.view.you ?? 0;
    // (A party of three or four plays a party's lawn: docs/PARTY.md.)
    this.lawn = matchLawn(
      this.data_.seed,
      this.data_.size,
      session.view.mode ?? 'duel',
      Math.max(2, session.view.players.length),
    );
    this.layoutCamera();
    this.scale.on(Phaser.Scale.Events.RESIZE, this.layoutCamera);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off(Phaser.Scale.Events.RESIZE, this.layoutCamera);
      this.unsubscribe?.();
      this.unsubscribe = null;
      this.scene.stop('Hud');
      this.scene.stop('Results');
      gameAudio.stopMower();
    });

    this.match = startMatch(this.lawn, matchRulesFor(session.view.rules));
    this.mine = seatView(this.match, this.you);
    const save = saves().save;
    const level = this.lawn.def;
    this.view = new LevelView(this, this.mine, level.art, {
      reducedShake: save.settings.reducedShake,
      // A soccer pitch is in the stadium; head to head's lawn by its level.
      biome: level.biome ?? biomeForLevel(level.level ?? 1).id,
      paint: equippedPaint(save, this.lawn.loaner),
      stripes: equippedStripes(save),
      outline: MINE_OUTLINE,
    });
    this.view.applyEvents(this.match.drainEvents());
    this.grassMarks = this.add.graphics().setDepth(MOWER_DEPTH - 3);
    // (Two against two: the teammate in green, the other team in red and blue.)
    const teams = !!this.lawn.def.sim.soccer && (this.lawn.def.sim.seats?.length ?? 2) === 4;
    const rivals = this.otherSeats().filter((s) => !teams || s % 2 !== this.you % 2);
    this.others = this.otherSeats().map((seat) => {
      const mate = teams && seat % 2 === this.you % 2;
      const i = rivals.indexOf(seat);
      const color = mate ? TEAMMATE_GREEN : (OTHER_COLORS[i] ?? FRIEND_RED);
      const grass = mate ? TEAMMATE_GRASS : (OTHER_GRASS[i] ?? OTHER_GRASS[0]);
      const texture = this.mowerTextureOf(seat);
      return {
        seat,
        color,
        grass,
        hatching: this.add
          .blitter(0, 0, this.hatchTexture(grass))
          .setAlpha(HATCH_ALPHA)
          .setDepth(MOWER_DEPTH - 3),
        outline: new SpriteOutline(
          this,
          texture,
          [
            { color: FRIEND_HALO, offsets: RING_2 },
            { color, offsets: RING_1 },
          ],
          MOWER_DEPTH - 1,
        ),
        mower: this.add.sprite(0, 0, texture, 0).setDepth(MOWER_DEPTH - 1),
      };
    });
    this.markPens();
    const middle = this.middle();
    this.star = middle ? new MazeStar(this, middle.x, middle.y, MOWER_DEPTH + 1) : null;
    this.peek = this.maze() ? this.drawPeek() : null;
    this.youMark = this.makeYouMark();
    this.youMarkUntil = Infinity;
    this.stars = this.add.graphics().setDepth(MOWER_DEPTH + 1);
    this.drawGrassMarks();

    new InputController(
      this,
      loadControlScheme(save.settings.controls),
      { onActivity: () => false, onIntent: this.onIntent },
      (y) => (y < HUD_HEIGHT ? 'ui' : null),
    );
    const keyboard = this.input.keyboard;
    keyboard?.on('keydown-ESC', () => this.confirmLeave());
    if (import.meta.env.DEV) keyboard?.on('keydown-P', () => (this.autopilot = !this.autopilot));

    this.scene.launch('Hud', { onMenu: () => this.confirmLeave() });
    this.scene.bringToTop('Hud');
    this.hud()?.hideMessage();
    this.unsubscribe = session.subscribe(this.onView);
    this.onView(session.view);
    exposeDebug({ scene: this, sim: this.match });
  }

  override update(): void {
    const elapsed = Date.now() - this.data_.atLocal;
    if (this.phase === 'countdown') {
      if (elapsed < 0) {
        const count = String(Math.ceil(-elapsed / 1000));
        if (count !== this.shownCount) {
          // A round-up: what to do, for both players (owner, 2026-10-29: only the inviter saw it).
          if (this.shownCount === '' && this.roundup()) {
            this.hud()?.showCaption('Nudge sheep into YOUR pen!');
          }
          this.shownCount = count;
          this.hud()?.showCountdown(count);
          gameAudio.play('click', 1 + (3 - Number(count)) * 0.12);
        }
      } else {
        this.go();
      }
    }
    const target = Math.max(0, Math.floor((elapsed * TICK_RATE) / 1000));
    if (this.phase === 'playing' || this.phase === 'over') this.play(target);
    const alpha = this.phase === 'playing' ? ((elapsed * TICK_RATE) / 1000) % 1 : 0;
    this.view.render(alpha, this.phase === 'playing');
    this.drawOthers(alpha);
    this.drawYouMark(alpha);
    this.drawStars();
    this.drawPlaza();
    this.updateHud();
  }

  /**
   * A hedge maze: the whole maze through the countdown (docs/MAZE_MATCH.md: a look to plan the way
   * before the camera follows the mower), with both corners and the middle marked.
   */
  private drawPeek(): Phaser.GameObjects.Container {
    const { width, height } = this.scale.gameSize;
    const level = this.lawn.def;
    const { width: w, height: h } = level.sim;
    // Below the 3-2-1 (the HUD draws it about a third of the way down).
    const top = Math.round((height - HUD_HEIGHT) * 0.3) + 24;
    const room = height - HUD_HEIGHT - top - 22;
    const px = Math.max(2, Math.min(Math.floor((width - 20) / w), Math.floor(room / h)));
    const key = lawnThumbnail(this, { ...level, id: `${level.id}:${this.data_.seed}` }, px, false);
    const mapW = w * px;
    const mapH = h * px;
    const x0 = Math.round((width - mapW) / 2);
    const y0 = top + 12;
    const g = this.add.graphics();
    navyPanel(g, x0 - 6, top - 4, mapW + 12, mapH + 22);
    const words = label(this, width / 2, top, 'FIRST TO THE STAR WINS!', 1, UI.accent).setOrigin(
      0.5,
      0,
    );
    const map = this.add.image(x0, y0, key).setOrigin(0);
    const dots = this.add.graphics();
    const dot = (x: number, y: number, color: number) =>
      dots.fillStyle(color).fillRect(x0 + x * px + 1, y0 + y * px + 1, px - 2, px - 2);
    const middle = this.middle();
    if (middle) dot(middle.x, middle.y, PLAZA_MARK);
    const seats = level.sim.seats ?? [];
    seats.forEach((s, seat) => dot(s.x, s.y, this.colorOf(seat)));
    return this.add
      .container(0, 0, [g, words, map, dots])
      .setScrollFactor(0)
      .setDepth(MOWER_DEPTH + 10);
  }

  /**
   * A hedge maze: the middle tile, where the star floats (the plaza is it and the paving beside
   * it: onto the paving is touching the star).
   */
  private middle(): { x: number; y: number } | null {
    const sim = this.lawn.def.sim;
    return sim.maze ? { x: (sim.width - 1) / 2, y: (sim.height - 1) / 2 } : null;
  }

  /** A hedge maze: the star bobbing (or with its winner), and the star and friend on the minimap. */
  private drawPlaza(): void {
    const middle = this.middle();
    if (!this.star || !middle) return;
    this.star.update(this.time.now);
    this.view.setMinimapMarks([
      { x: middle.x, y: middle.y, color: PLAZA_MARK },
      ...this.others.map((o) => ({
        x: o.mower.x / TILE_SIZE - 0.5,
        y: o.mower.y / TILE_SIZE - 0.5,
        color: o.color,
      })),
    ]);
  }

  // ── The match ──────────────────────────────────────────────────────────────────────────────

  private go(): void {
    this.phase = 'playing';
    this.youMarkUntil = this.time.now + YOU_MARK_MS;
    this.hud()?.showCountdown('GO!');
    gameAudio.play('click', 1.5);
    this.time.delayedCall(350, () => this.hud()?.hideCountdown());
    if (this.roundup())
      this.time.delayedCall(RULES_CAPTION_MS, () => this.hud()?.showCaption(null));
    // A hedge maze: the whole maze goes, and the camera follows the mower.
    const peek = this.peek;
    this.peek = null;
    if (peek) {
      this.tweens.add({ targets: peek, alpha: 0, duration: 250, onComplete: () => peek.destroy() });
    }
    gameAudio.startMower(this.mine, this.lawn.loaner);
    // Mower soccer: the referee's peep for the kick-off (and the shepherd's for a round-up).
    if (this.soccer() || this.roundup()) gameAudio.kickOff();
    if (this.queuedTurn) this.turn(this.queuedTurn);
    this.queuedTurn = null;
    this.view.previewTurn(null);
  }

  /**
   * On to `target`: rebuilt from the start when a stamped turn landed on a tick already played,
   * else played on (this player's newest turns included).
   */
  private play(target: number): void {
    if (this.session.lateFrom <= this.match.tick) {
      this.rebuild(target);
      return;
    }
    while (this.match.status === 'running' && this.match.tick < target) {
      if (this.autopilot) this.steerAutopilot();
      playMatchTo(this.match, this.session.matchTurns(), this.match.tick + 1, (events) =>
        this.handleEvents(events),
      );
      if (this.match.tick - this.lastProgress >= PROGRESS_EVERY) this.sendProgress();
    }
    gameAudio.updateMower(this.mine);
    if (this.match.status !== 'running' && this.phase === 'playing') this.matchOverHere();
  }

  /** The match again from its first tick with every turn known, the lawn redrawn to match. */
  private rebuild(target: number): void {
    this.session.lateFrom = Infinity;
    this.match = playMatch(
      this.lawn,
      this.session.matchTurns(),
      target,
      matchRulesFor(this.session.view.rules),
    );
    this.match.drainEvents();
    this.mine = seatView(this.match, this.you);
    this.view.resync(this.mine);
    this.drawGrassMarks();
    this.planner = null;
    this.kicker = null;
    this.herder = null;
    this.mazer = null;
    exposeDebug({ scene: this, sim: this.match });
    if (this.match.status !== 'running' && this.phase === 'playing') this.matchOverHere();
  }

  /** A turn of this player's: into the match at the next tick, and to the room. */
  private turn(intent: Intent): void {
    if (intent.type !== 'face' && intent.type !== 'turn') return;
    if (this.match.status !== 'running' || this.match.seatStatus(this.you) !== 'running') return;
    this.session.sendInput(this.match.tick + 1, encodeIntent(intent));
  }

  private readonly onIntent = (intent: Intent): void => {
    if (this.phase === 'countdown' && (intent.type === 'face' || intent.type === 'turn')) {
      this.queuedTurn = intent;
      if (intent.type === 'face') this.view.previewTurn(intent);
      return;
    }
    if (this.phase === 'playing' && !this.autopilot) this.turn(intent);
  };

  private sendProgress(): void {
    this.lastProgress = this.match.tick;
    this.session.sendProgress(this.match.tick, this.share(this.you), hashSimState(this.match));
  }

  /** A seat's share of the lawn's grass, in percent. */
  private share(seat: number): number {
    return Math.round((this.match.seatTiles(seat) * 100) / Math.max(1, this.match.grid.totalGrass));
  }

  /** The match has ended on this phone: the room's own replay decides, a moment later. */
  private matchOverHere(): void {
    this.phase = 'over';
    // A hedge maze: whoever touched the star first takes it.
    const winner = this.match.match?.winner;
    if (this.star && this.match.match?.why === 'maze' && winner != null) {
      const seat = winner;
      this.star.winTo(() => {
        const m = seatView(this.match, seat).mower;
        return { x: (m.x + 0.5) * TILE_SIZE, y: (m.y + 0.5) * TILE_SIZE };
      });
    }
    gameAudio.stopMower();
    if (!this.session.view.match) this.hud()?.showMessage('Waiting for the result...');
  }

  private handleEvents(all: readonly SimEvent[]): void {
    const others = new Set<number>(this.otherSeats());
    // The others' own mower happenings are theirs; what they do to the lawn is everyone's.
    // (A soccer ball's kicks, goals and resets are everyone's too, and a round-up's sheep.)
    const lawn = new Set([
      'tileMowed',
      'pickup',
      'trampled',
      'smashed',
      'kicked',
      'goal',
      'ballReset',
      'nudged',
    ]);
    const shared = new Set(['goal', 'kicked', 'nudged']);
    const events = all.filter(
      (e) => e.seat === undefined || !others.has(e.seat) || lawn.has(e.type),
    );
    this.view.applyEvents(events);
    gameAudio.onEvents(
      events.filter((e) => e.seat === this.you || shared.has(e.type)),
      this.mine,
    );
    for (const e of all) {
      if (e.type !== 'nudged' || e.pen === undefined) continue;
      // A round-up: a sheep into a pen (whoever nudged it, it counts for the pen's seat).
      const [mine, theirs] = this.score();
      if (e.pen === this.you) {
        haptics.heavy();
        this.hud()?.showNotice(`PENNED! ${mine}-${theirs}`);
      } else {
        this.hud()?.showNotice(`${this.shortOf(e.pen)} PENS ONE! ${mine}-${theirs}`);
      }
      this.herder = null;
      this.mazer = null;
    }
    for (const e of all) {
      if (e.type !== 'goal') continue;
      // Mower soccer: whose goal it was, and the score.
      const [mine, theirs] = this.score();
      // (Two against two: a goal is credited to its team's first seat.)
      const ours = this.match.teams ? (e.seat ?? -1) % 2 === this.you % 2 : e.seat === this.you;
      if (ours) {
        haptics.heavy();
        this.hud()?.showNotice(`GOAL! ${mine}-${theirs}`);
      } else if (this.match.teams) {
        this.hud()?.showNotice(`THEY SCORE! ${mine}-${theirs}`);
      } else {
        this.hud()?.showNotice(`${this.shortOf(e.seat ?? 0)} SCORES! ${mine}-${theirs}`);
      }
      this.kicker = null;
    }
    for (const e of all) {
      if (e.type === 'tileMowed' && e.fresh && e.seat !== undefined && others.has(e.seat)) {
        this.drawGrassMarks();
      }
      if (e.seat === this.you) {
        if (e.type === 'turned') haptics.tap();
        else if (e.type === 'crashed') haptics.impact();
        else if (e.type === 'bumped') {
          haptics.impact();
          this.hud()?.showNotice('BUMP!');
        } else if (e.type === 'dizzy') {
          haptics.heavy();
          gameAudio.play('bump', 0.8);
          this.hud()?.showNotice('DIZZY!');
          this.planner = null;
          this.kicker = null;
          this.herder = null;
          this.mazer = null;
        } else if (e.type === 'mowerBroken') haptics.heavy();
      } else if (e.seat !== undefined && others.has(e.seat)) {
        if (e.type === 'bumped') {
          this.planner = null;
          this.kicker = null;
          this.herder = null;
          this.mazer = null;
        } else if (e.type === 'dizzy') {
          gameAudio.play('bump', 0.8);
          this.hud()?.showNotice(`${this.shortOf(e.seat)} IS DIZZY!`);
        }
      }
    }
  }

  /** A tile of diagonal lines in a color (made once), every {@link HATCH_GAP} pixels. */
  private hatchTexture(color: number): string {
    const key = `${HATCH_KEY}-${color.toString(16)}`;
    if (this.textures.exists(key)) return key;
    const canvas = this.textures.createCanvas(key, TILE_SIZE, TILE_SIZE);
    if (!canvas) return key;
    const ctx = canvas.getContext();
    ctx.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
    for (let y = 0; y < TILE_SIZE; y++) {
      for (let x = 0; x < TILE_SIZE; x++) {
        if ((x + y) % HATCH_GAP === HATCH_GAP - 1) ctx.fillRect(x, y, 1, 1);
      }
    }
    canvas.refresh();
    return key;
  }

  /**
   * The others' grass (who owns what): faint diagonals in each one's color, and a faint line
   * inside its edge wherever it meets grass that is not theirs. Redrawn whole on each tile they
   * take (a few hundred tiles at most).
   */
  private drawGrassMarks(): void {
    const g = this.grassMarks.clear();
    const { width, height } = this.match.grid;
    const T = TILE_SIZE;
    for (const other of this.others) {
      other.hatching.clear();
      const theirs = (x: number, y: number) => this.match.ownerAt(x, y) === other.seat;
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          if (!theirs(x, y)) continue;
          const px = x * T;
          const py = y * T;
          other.hatching.create(px, py);
          g.fillStyle(other.grass, FRIEND_EDGE_ALPHA);
          if (y === 0 || !theirs(x, y - 1)) g.fillRect(px, py, T, 1);
          if (y === height - 1 || !theirs(x, y + 1)) g.fillRect(px, py + T - 1, T, 1);
          if (x === 0 || !theirs(x - 1, y)) g.fillRect(px, py, 1, T);
          if (x === width - 1 || !theirs(x + 1, y)) g.fillRect(px + T - 1, py, 1, T);
        }
      }
    }
  }

  private drawOthers(alpha: number): void {
    for (const other of this.others) {
      const seat = other.seat;
      const them = seatView(this.match, seat);
      const m = them.mower;
      const going = this.match.status === 'running' && this.match.seatStatus(seat) === 'running';
      const dizzy = them.dizzy > 0;
      const willAdvance = going && !dizzy && !them.blocks(m.x + dx(m.dir), m.y + dy(m.dir));
      const { px, py } = mowerPixelPosition(
        m,
        them.crossTicks(),
        going && !dizzy ? alpha : 0,
        willAdvance,
      );
      const spin = dizzy ? Math.floor(this.time.now / DIZZY_SPIN_MS) % 4 : 0;
      const frame = (m.dir + spin) % 4;
      // Two against two: the other team see-through, like a ghost, so the teammate stands out
      // (owner, 2026-10-29). A mower that broke down is fainter still.
      const rival = this.match.teams && seat % 2 !== this.you % 2;
      const shown =
        (this.match.seatStatus(seat) === 'running' ? 1 : 0.5) * (rival ? RIVAL_ALPHA : 1);
      other.mower.setPosition(px, py).setFrame(frame).setAlpha(shown);
      // (A see-through mower's outline would show through it as a solid blob: a plain ghost.)
      other.outline.follow(other.mower, !rival);
    }
  }

  /** The seats of every other mower in this match, in seat order. */
  private otherSeats(): Seat[] {
    const n = this.lawn.def.sim.seats?.length ?? 2;
    return Array.from({ length: n }, (_, s) => s as Seat).filter((s) => s !== this.you);
  }

  /** A seat's color: this player's yellow, the others' in seat order. */
  private colorOf(seat: number): number {
    if (seat === this.you) return MINE_OUTLINE;
    return this.others.find((o) => o.seat === seat)?.color ?? FRIEND_RED;
  }

  /** A seat's mower in its owner's paint (on the mower this match lends), or plain. */
  private mowerTextureOf(seat: Seat): string {
    const player = this.session.view.players.find((p) => p.seat === seat);
    const paint = knownPaints(player?.paints)[this.lawn.loaner];
    return paint
      ? paintedMowerTexture(this, this.lawn.loaner, paint)
      : mowerTextureKey(this.lawn.loaner);
  }

  /** A little "YOU" tag with a pointer, in this player's colors. */
  private makeYouMark(): Phaser.GameObjects.Container {
    const tag = label(this, 0, 0, 'YOU', 1, UI.accent).setOrigin(0.5, 1);
    const w = Math.ceil(tag.width) + 4;
    const box = this.add.graphics();
    box.fillStyle(UI.panel, 1).fillRect(-w / 2, -tag.height - 2, w, tag.height + 3);
    box
      .lineStyle(1, UI.accent, 1)
      .strokeRect(-w / 2 + 0.5, -tag.height - 1.5, w - 1, tag.height + 2);
    // The pointer down to the mower.
    box.fillStyle(UI.accent, 1).fillRect(-2, 2, 5, 1).fillRect(-1, 3, 3, 1).fillRect(0, 4, 1, 1);
    return this.add.container(0, 0, [box, tag]).setDepth(MOWER_DEPTH + 2);
  }

  /**
   * A round-up: each pen lined in its player's color (this player's yellow, the friend's red), and
   * "YOURS" over this player's own for the first moments. Nobody's screen is turned round, so the
   * friend's own pen is at the top.
   */
  private markPens(): void {
    this.penTag = null;
    const pens = this.lawn.def.sim.roundup?.pens;
    if (!pens) return;
    const g = this.add.graphics().setDepth(MOWER_DEPTH - 1);
    pens.forEach(({ pen }, seat) => {
      const xs = pen.map((p) => p.x);
      const ys = pen.map((p) => p.y);
      const x = Math.min(...xs) * TILE_SIZE + 4;
      const y = Math.min(...ys) * TILE_SIZE + 4;
      const w = (Math.max(...xs) + 1) * TILE_SIZE - 4 - x;
      const h = (Math.max(...ys) + 1) * TILE_SIZE - 4 - y;
      g.lineStyle(1, this.colorOf(seat), 1);
      g.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
      if (seat !== this.you) return;
      const tag = label(this, 0, 0, 'YOURS', 1, UI.accent).setOrigin(0.5, 0.5);
      const tw = Math.ceil(tag.width) + 4;
      const th = Math.ceil(tag.height) + 3;
      const box = this.add.graphics();
      box.fillStyle(UI.panel, 1).fillRect(-tw / 2, -th / 2, tw, th);
      box.lineStyle(1, UI.accent, 1).strokeRect(-tw / 2 + 0.5, -th / 2 + 0.5, tw - 1, th - 1);
      this.penTag = this.add
        .container(Math.round(x + w / 2), Math.round(y + h / 2), [box, tag])
        .setDepth(MOWER_DEPTH + 2);
    });
  }

  private drawYouMark(alpha: number): void {
    const mark = this.youMark;
    if (!mark) return;
    if (this.time.now > this.youMarkUntil || this.phase === 'result') {
      mark.destroy();
      this.youMark = null;
      this.penTag?.destroy();
      this.penTag = null;
      return;
    }
    const m = this.mine.mower;
    const going = this.phase === 'playing' && this.match.status === 'running';
    const willAdvance = going && !this.mine.blocks(m.x + dx(m.dir), m.y + dy(m.dir));
    const { px, py } = mowerPixelPosition(
      m,
      this.mine.crossTicks(),
      going ? alpha : 0,
      willAdvance,
    );
    // A gentle bob, a pixel at a time.
    const bob = Math.floor(this.time.now / 300) % 2;
    mark.setPosition(Math.round(px), Math.round(py) - 11 - bob);
  }

  /** Three little stars circling the head of each dizzy mower. */
  private drawStars(): void {
    this.stars.clear();
    if (this.match.status !== 'running') return;
    for (let seat = 0; seat < this.match.seats; seat++) {
      if (this.match.seatDizzy(seat) === 0) continue;
      const them = seatView(this.match, seat);
      const { px, py } = mowerPixelPosition(them.mower, them.crossTicks(), 0, false);
      const turn = this.time.now / 240;
      for (let k = 0; k < 3; k++) {
        const a = turn + (k * Math.PI * 2) / 3;
        const x = Math.round(px + Math.cos(a) * 7);
        const y = Math.round(py - 12 + Math.sin(a) * 2);
        // A plus-shaped pixel star with a dark shadow, so it shows over any paint or grass.
        for (const [color, off] of [
          [0x1d1d2b, 1],
          [0xfff7c0, 0],
        ] as const) {
          this.stars.fillStyle(color, 1);
          this.stars.fillRect(x - 1, y + off, 3, 1);
          this.stars.fillRect(x, y - 1 + off, 1, 3);
        }
      }
    }
  }

  private steerAutopilot(): void {
    if (this.mine.mower.progress !== 0 || this.mine.dizzy > 0) return;
    if (this.soccer()) {
      this.kicker ??= new SoccerPilot(this.mine, this.match.attackedMouths(this.you));
      const turn = this.kicker.decide();
      if (turn) this.turn({ type: 'turn', turn });
      return;
    }
    if (this.maze()) {
      this.mazer ??= new MazePilot(this.mine);
      const intent = this.mazer.decide();
      if (intent) this.turn(intent);
      return;
    }
    const pen = this.match.homePen(this.you);
    if (pen) {
      this.herder ??= new HerdPilot(this.mine, pen);
      const turn = this.herder.decide();
      if (turn) this.turn({ type: 'turn', turn });
      return;
    }
    this.planner ??= new GreedyPlanner(this.mine);
    const action = this.planner.nextAction();
    if (action && action !== 'straight') this.turn({ type: 'turn', turn: action });
  }

  // ── The room ───────────────────────────────────────────────────────────────────────────────

  private readonly onView = (view: RaceView): void => {
    if (!this.scene.isActive()) return;
    // A rematch: the new lawn's countdown.
    if (view.start && view.start.seed !== this.data_.seed) {
      this.scene.restart({
        seed: view.start.seed,
        size: view.size ?? this.data_.size,
        atLocal: view.start.atLocal,
      } satisfies DuelLevelData);
      return;
    }
    if (view.match && !this.settled) {
      this.showResult(view.match);
      return;
    }
    if (view.friendLeft && !this.noticed.left && this.phase !== 'result') {
      this.noticed.left = true;
      this.hud()?.showNotice(
        this.others.length > 1
          ? 'A PLAYER LEFT'
          : `${this.shortOf(this.others[0]?.seat ?? 0)} LEFT`,
      );
    }
    if (view.line === 'reconnecting' && !this.noticed.line) {
      this.noticed.line = true;
      this.hud()?.showNotice('RECONNECTING...');
    } else if (view.line === 'open') this.noticed.line = false;
  };

  /** The room's verdict: who won, the grass and coins each took, and what this player is paid. */
  private showResult(result: MatchResult): void {
    this.settled = true;
    this.phase = 'result';
    gameAudio.stopMower();
    this.hud()?.hideMessage();
    const you = this.you;
    const friend = (this.others[0]?.seat ?? 1 - you) as Seat;
    const name = this.friendName();
    const today = todayKey();
    const save = saves().save;
    const paidToday = save.race.day === today ? save.race.paid : 0;
    const party = result.tiles.length > 2;
    const places =
      result.places ??
      result.tiles.map((_, s) => (result.winner === null || result.winner === s ? 1 : 2));
    const place = places[you] ?? places.length;
    const pay = matchPay(
      this.data_.size,
      you,
      result.winner,
      result.coins[you] ?? 0,
      paidToday,
      party ? places : undefined,
    );
    const won = party ? place === 1 : result.winner === you;
    saves().update((s) => {
      if (s.race.day !== today) s.race = { ...s.race, day: today, paid: 0 };
      s.race.paid++;
      s.race.races++;
      if (won) s.race.wins++;
      s.coins += pay.bonus + pay.coins;
    });
    if (won) haptics.heavy();
    if (party) {
      this.showPartyResult(result, places, pay, paidToday);
      return;
    }
    const total = Math.max(1, this.match.grid.totalGrass);
    const pct = (seat: Seat) => Math.round(((result.tiles[seat] ?? 0) * 100) / total);
    const loser = result.winner === null ? null : result.winner === you ? name : 'You';
    const goals = result.goals;
    const penned = result.penned;
    const distance = result.distance;
    const why =
      result.why === 'maze'
        ? result.winner === null
          ? 'You both touched the star at once.'
          : `${result.winner === you ? 'You' : name} touched the star first.`
        : result.why === 'goals'
          ? `${result.winner === you ? 'You' : name} scored ${this.lawn.def.sim.soccer?.target ?? ''}.`
          : result.why === 'sheep'
            ? `${result.winner === you ? 'You' : name} penned ${this.lawn.def.sim.roundup?.target ?? ''}.`
            : result.why === 'time'
              ? result.winner === null
                ? 'Level at the whistle.'
                : 'The whistle went.'
              : result.why === 'half'
                ? `${result.winner === you ? 'You' : name} mowed more than half.`
                : result.why === 'done'
                  ? 'The whole lawn is mowed.'
                  : result.why === 'brokeDown'
                    ? `${loser ?? 'Both'} broke down.`
                    : `${loser === 'You' ? 'You' : name} left the match.`;
    const title =
      result.winner === you ? 'You won!' : result.winner === null ? 'A draw!' : `${name} won`;
    const other = this.session.view.players.find((p) => p.seat === friend)?.player;
    const befriend = other && !isFriend(other) && other !== saves().save.online.player?.id;
    const rematch = !this.session.view.friendLeft;
    this.resultCard = {
      title,
      ...(result.winner === you ? { cheer: true } : {}),
      info: goals
        ? [
            `You: ${plural(goals[you] ?? 0, 'goal')}`,
            `${name}: ${plural(goals[friend] ?? 0, 'goal')}`,
            why,
          ]
        : penned
          ? [`You: ${penned[you] ?? 0} sheep`, `${name}: ${penned[friend] ?? 0} sheep`, why]
          : distance
            ? [`You: ${toGo(distance[you] ?? 0)}`, `${name}: ${toGo(distance[friend] ?? 0)}`, why]
            : [
                `You: ${result.tiles[you] ?? 0} tiles (${pct(you)}%)`,
                `${name}: ${result.tiles[friend] ?? 0} tiles (${pct(friend)}%)`,
                why,
                `Coins: ${result.coins[you] ?? 0} to ${result.coins[friend] ?? 0}`,
              ],
      coinLines: [
        ...(pay.bonus > 0
          ? [{ label: result.winner === null ? 'Draw bonus' : 'Win bonus', coins: pay.bonus }]
          : []),
        ...(pay.coins > 0 ? [{ label: 'Coins', coins: pay.coins }] : []),
      ],
      wallet: saves().save.coins,
      ...(paidToday >= RACE_PAID_PER_DAY ? { note: "Today's paid matches are done." } : {}),
      buttons: [
        { label: 'Done', action: () => this.done() },
        ...(befriend ? [{ label: 'Add friend', action: () => void this.befriend(other) }] : []),
        ...(rematch ? [{ label: 'Rematch', action: () => this.askRematch(), primary: true }] : []),
      ],
    };
    this.showCard(this.resultCard);
  }

  /**
   * A party's result (docs/PARTY.md): everyone by place with their score, why it ended, and this
   * player's bonus by place.
   */
  private showPartyResult(
    result: MatchResult,
    places: readonly number[],
    pay: { bonus: number; coins: number },
    paidToday: number,
  ): void {
    const you = this.you;
    const place = places[you] ?? places.length;
    const shared = places.filter((p) => p === place).length > 1;
    // (Short: the title is big type, about 13 characters a line.)
    const allFirst = places.every((p) => p === 1);
    const title = this.match.teams
      ? allFirst
        ? 'A draw!'
        : place === 1
          ? 'We won!'
          : 'They won'
      : place === 1
        ? shared
          ? 'You tied!'
          : 'You won!'
        : `${ordinal(place)} place`;
    const order = places
      .map((p, seat) => ({ p, seat }))
      .sort((a, b) => a.p - b.p || a.seat - b.seat);
    const lines = order.map(
      ({ p, seat }) =>
        `${ordinal(p)} ${seat === you ? 'You' : this.nameOf(seat)}: ${this.scoreText(result, seat)}`,
    );
    const why =
      result.why === 'forfeit'
        ? 'A player left the match.'
        : result.why === 'time'
          ? 'The whistle went.'
          : result.why === 'brokeDown'
            ? 'Mowers broke down.'
            : result.why === 'maze'
              ? 'The star was touched.'
              : result.why === 'lead'
                ? 'Nobody could catch up.'
                : result.why === 'done'
                  ? 'The whole lawn is mowed.'
                  : '';
    const rematch = !this.session.view.friendLeft;
    this.resultCard = {
      title,
      ...(place === 1 ? { cheer: true } : {}),
      info: [...lines, ...(why ? [why] : [])],
      coinLines: [
        ...(pay.bonus > 0
          ? [{ label: place === 1 ? 'Win bonus' : 'Place bonus', coins: pay.bonus }]
          : []),
        ...(pay.coins > 0 ? [{ label: 'Coins', coins: pay.coins }] : []),
      ],
      wallet: saves().save.coins,
      ...(paidToday >= RACE_PAID_PER_DAY ? { note: "Today's paid matches are done." } : {}),
      buttons: [
        { label: 'Done', action: () => this.done() },
        ...(rematch ? [{ label: 'Rematch', action: () => this.askRematch(), primary: true }] : []),
      ],
    };
    this.showCard(this.resultCard);
  }

  /** A seat's score in the result, in words: grass, goals, sheep, or how near the middle. */
  private scoreText(result: MatchResult, seat: number): string {
    if (result.goals) return plural(result.goals[seat] ?? 0, 'goal');
    if (result.penned) return `${result.penned[seat] ?? 0} sheep`;
    if (result.distance) {
      const d = result.distance[seat] ?? 0;
      return d === 0 ? 'the star' : `${plural(d, 'tile')} off`;
    }
    const total = Math.max(1, this.match.grid.totalGrass);
    return `${Math.round(((result.tiles[seat] ?? 0) * 100) / total)}%`;
  }

  private async befriend(id: string): Promise<void> {
    // A request they accept (docs/FRIENDS.md, step 3), unless they asked first.
    const result = await addFriend({ player: id });
    const card = this.resultCard;
    if (!card || this.phase !== 'result') return;
    this.resultCard = {
      ...card,
      note:
        typeof result === 'string'
          ? result
          : result.pending
            ? 'Friend request sent.'
            : `Friends with ${whoText(result.info.player, result.info.name)}!`,
      buttons: card.buttons.filter((b) => b.label !== 'Add friend'),
    };
    this.showCard(this.resultCard);
  }

  private askRematch(): void {
    this.session.rematch();
    this.scene.stop('Results');
    this.hud()?.showMessage(`Waiting for ${this.friendName()}...`);
  }

  /** Android's back button: DONE on the result card, otherwise asked first, as the pause button. */
  onBack(): void {
    if (this.phase === 'result' && this.scene.isActive('Results')) this.done();
    else this.confirmLeave();
  }

  /** The pause button (or Esc): leaving is always asked first; mid-match it is a forfeit. */
  private confirmLeave(): void {
    if (this.phase === 'result' && this.scene.isActive('Results')) return;
    const name = this.friendName();
    const [info, stay] =
      this.phase === 'result'
        ? [`Stop waiting for ${name}?`, 'Keep waiting']
        : this.phase === 'over'
          ? ['The match is over. Leave now and you miss the result.', 'Stay']
          : [
              this.others.length > 1
                ? 'You forfeit: last place.'
                : `You forfeit, and ${name} wins.`,
              'Keep mowing',
            ];
    this.showCard({
      title: 'Leave the match?',
      info: [info],
      coinLines: [],
      buttons: [
        { label: 'Leave', action: () => this.done() },
        { label: stay, action: () => this.scene.stop('Results'), primary: true },
      ],
    });
  }

  private done(): void {
    this.session.leave();
    wipeTo(this, 'Versus');
  }

  private showCard(model: ResultsModel): void {
    this.scene.stop('Results');
    this.scene.launch('Results', model);
    this.scene.bringToTop('Results');
  }

  /** The other player in a match of two; in a party, "everyone". */
  private friendName(): string {
    if (this.others.length > 1) return 'everyone';
    return this.nameOf(this.others[0]?.seat ?? 1 - this.you);
  }

  /** A seat's player, by nickname or name. */
  private nameOf(seat: number): string {
    const player = this.session.view.players.find((p) => p.seat === seat);
    return player ? whoText(player.player, player.name) : 'Your friend';
  }

  /** A seat's player in a word (for notices): a nickname, or the last word of the name. */
  private shortOf(seat: number): string {
    const player = this.session.view.players.find((p) => p.seat === seat);
    // A nickname fits (at most 10 characters); a name gives its last word.
    const nickname = nicknameOf(player?.player);
    if (nickname) return nickname;
    const name = player?.name;
    const noun = name?.kind === 'words' ? NAME_NOUNS[name.parts[1]] : undefined;
    return (noun ?? 'FRIEND').toUpperCase();
  }

  /** Mower soccer: whether this match is one. */
  private soccer(): boolean {
    return !!this.lawn.def.sim.soccer;
  }

  /** A sheep round-up: whether this match is one. */
  private maze(): boolean {
    return !!this.lawn.def.sim.maze;
  }

  private roundup(): boolean {
    return !!this.lawn.def.sim.roundup;
  }

  /**
   * Mower soccer: [this player's goals, the best of the others']; a round-up: the sheep in each
   * pen, the same way.
   */
  private score(): [number, number] {
    const score = this.roundup() ? this.match.penCounts : this.match.soccerGoals;
    // (Two against two: the other team's goals.)
    if (this.match.teams) return [score[this.you] ?? 0, score[(this.you + 1) % 2] ?? 0];
    const theirs = Math.max(0, ...this.others.map((o) => score[o.seat] ?? 0));
    return [score[this.you] ?? 0, theirs];
  }

  /** A party: this player's place right now, by each mode's score. */
  private placeNow(): number {
    const sim = this.match;
    const n = this.others.length + 1;
    const seats = Array.from({ length: n }, (_, s) => s);
    const scores = this.maze()
      ? sim.mazeDistances
      : this.roundup()
        ? sim.penCounts
        : this.soccer()
          ? sim.soccerGoals
          : seats.map((s) => sim.seatTiles(s));
    return placesFrom([...scores], this.maze() ? 'lower' : 'higher')[this.you] ?? n;
  }

  private updateHud(): void {
    const m = this.mine.mower;
    const spec = this.mine.mowerSpec;
    // Soccer and the round-up: the score and the seconds to the whistle; head to head: the
    // friend's share.
    const sim = this.lawn.def.sim;
    const whistle = sim.soccer?.matchTicks ?? sim.roundup?.matchTicks ?? sim.maze?.matchTicks;
    const [mine, theirs] = this.score();
    const left =
      whistle === undefined ? 0 : Math.max(0, Math.ceil((whistle - this.match.tick) / 60));
    // A party: this player's place now (two against two: the score, as for two).
    const place =
      this.others.length > 1 && !this.match.teams
        ? `${ordinal(this.placeNow())} of ${this.others.length + 1}`
        : null;
    this.hud()?.setInfo({
      label: place
        ? whistle === undefined
          ? place
          : `${place}  ${left}s`
        : whistle === undefined
          ? `VS ${this.share(this.others[0]?.seat ?? 1 - this.you)}%`
          : sim.maze
            ? `MAZE  ${left}s`
            : `${mine}-${theirs}  ${left}s`,
      timer: 'plain',
      percent: this.share(this.you),
      hp: m.hp,
      maxHp: spec.maxHp,
      wallet: saves().save.coins,
      pending: this.phase === 'result' ? null : this.match.seatCoins(this.you),
      fuel: spec.fuelTiles === undefined ? null : m.fuel / spec.fuelTiles,
      slow: spec.throttle ? m.slow : null,
      tools: [],
      autopilot: this.autopilot,
      flat: m.flat,
    });
  }

  private hud(): HudScene | null {
    return this.scene.get('Hud') as HudScene | null;
  }

  private readonly layoutCamera = (): void => {
    const { width, height } = this.scale.gameSize;
    this.cameras.main.setViewport(0, HUD_HEIGHT, width, height - HUD_HEIGHT);
    this.cameras.main.setBackgroundColor(BACKGROUND);
  };
}
